<template>
  <div class="w-full flex flex-col">
    <div data-testid="signatory-matrix" :data-view="state.showLatest ? 'latest' : 'approved'" :data-inactive="state.showCancelled" :data-row-count="people?.length || 0" :data-has-revision="!!currentRevision?.Signatories" class="m-2 p-2 bg-white rounded shadow flex flex-col" style="height: 100%; max-height: 100%;" v-if="loaded">
      <div class="flex flex-row items-center">
        <p class="p-2 text-lg font-medium text-primary flex-grow">
          Appendix 2 - {{ state.showLatest ? "Unapproved" : "Approved" }} Signatory Matrix
        </p>
        <!-- <div class="
            mr-4
          " v-if="changesDetected">
          <div class="flex flex-row items-center">
            <p class="font-bold">TEST</p>
          </div>
        </div> -->
        <button class="primary mr-2" @click="openChangesPopup" v-if="state.showLatest">
          Changes
        </button>
        <IconMenu :actions="menuActions" :contextData="{ store, form, db, meta: route?.meta }" />
      </div>
      <div class="grid grid-cols-1 grid-gap-2 md:grid-cols-3">
        <Value label="Current Revision" :value="currentRevision?.Revision" v-if="!state.showLatest" />
        <Value label="Date Revised" :value="new Date(currentRevision?.Date).toLocaleDateString(
            'en-gb',
            standardDateLocal
          )
            " v-if="!state.showLatest" />
        <SignatoryKey />
      </div>
      <!-- <div v-if="state.showLatest" class="text-sm mt-1 px-1" :class="usersPendingSignOff.length ? 'text-orange' : 'text-green'">
        {{ usersPendingSignOff.length
          ? `${usersPendingSignOff.length} user(s) still need to sign off their entry changes before the next revision can be accepted.`
          : 'All users have signed off pending entry changes.' }}
      </div> -->
      <DataTable :tableSettings="{ ...signatoryMatrixTableConfig, columnState: signatoryMatrixTableConfig.columnDefs }"
        :items="people" @itemSelected="itemSelected" @selectionChanged="selectionChanged"
        :paginationPageSize="50" :key="state.showLatest" ref="table" />
      <div v-if="state.showLatest" class="mt-2 flex items-center justify-end border-t border-gray-200 pt-2">
        <p class="mr-3 text-sm text-gray-600">
          {{ selectedPeople.length }} selected
        </p>
        <button class="success" :disabled="selectedPeople.length === 0" @click="completeBulkApproval">
          Complete
        </button>
      </div>
    </div>
  </div>
  <div
    v-if="changesPopupOpen"
    class="fixed inset-0 z-50 flex items-center justify-center px-4"
    style="background: rgba(0, 0, 0, 0.4)"
    @click.self="closeChangesPopup"
  >
    <div class="bg-white rounded shadow p-4 border border-gray-100 w-full max-w-3xl max-h-[80vh] overflow-auto">
      <div class="flex flex-row items-center mb-3">
        <p class="p-2 font-medium flex-grow text-primary">Changes</p>
        <button class="cancel" @click="closeChangesPopup">Close</button>
      </div>
      <!-- <pre class="whitespace-pre-wrap text-sm text-text bg-gray-50 border border-gray-200 rounded p-3">{{ changesValue }}</pre> -->
      <Value label="Changes" :value="changesValue" grow />
    </div>
  </div>
  <DynamicForm ref="form" @submitted="formSubmitted" />
  <RevisionHistoryModal ref="revisionHistoryModal" />
</template>
<script setup>
import { ref, computed, onMounted, watch, reactive } from "vue";
import DynamicForm from "../../../components/DynamicForm/DynamicForm.vue";
import { DataTable, ConfirmationInput, IconMenu } from "@comp";
import _signatoryMatrixTableConfig from "./signatoryMatrixTableConfig.js";
import SignatoryKey from "./SignatoryKey.vue";
import RevisionHistoryModal from "./RevisionHistoryModal.vue";
import {
  addEmployee,
  bulkApprove,
  reviseSignatoryMatrix,
  downloadRevisionHistory
} from "./actions.js";
import changeIdentifier from "./changeIdentifier.js";
import { db } from "../../../db.js";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { matrixReport } from "./reports";
import { next, standardDateLocal } from "../../../utils";
import { isEqual } from "lodash";
import { has } from "lodash";
import {
  STC_VARIANT,
  MILITARY_VARIANT,
  signatoriesPath,
  panelsPath,
  approvedFunctionsPath,
  typesPath,
  revisionIdPath,
  revisionNumberPath,
  revisionSnapshotsPath,
  revisionRefsPath,
} from "../_variants/index.js";
import Value from "../../../../../resources/web-components/Inputs/Value.vue";
// import {
//   Value
// } from "@comps/index"
let form = ref(null);
const revisionHistoryModal = ref(null);
const changesPopupOpen = ref(false);

const store = useStore();
const router = useRouter(null);
const route = useRoute();
const table = ref(null);
const selectedPeople = ref([]);
const state = reactive({
  showCancelled: false,
  showLatest: route.meta.latest,
});

// Resolve variant from route.meta.formMetadata.ParentType
const variant = computed(() => 
  route.meta?.formMetadata?.ParentType === "MilitarySignatoryMatrix" ? MILITARY_VARIANT : STC_VARIANT
);

// Variant-specific Vuex state keys for current revision
const revisionStateKey = computed(() => 
  variant.value.key === "military" ? "currentMilitarySignatoryMatrixRevision" : "currentSignatoryMatrixRevision"
);

const people = computed(() => {
  state.showLatest = route.meta.latest;

  const signatoriesDataPath = signatoriesPath(variant.value);
  const liveSignatories = store.state[signatoriesDataPath];
  
  if (!liveSignatories) return null;

  const arrayOfSignatories = state.showLatest 
    ? Object.values(liveSignatories)
    : Object.values(store?.state?.[revisionStateKey.value]?.Signatories || {});

  return arrayOfSignatories
    .filter((i) => i.Cancelled === state.showCancelled)
    .map((form) => {
      const panels = Object.fromEntries(
        Object.entries(form.Panels || {}).map(([key, value]) => {
          return [key, value.Answer === "true"];
        })
      );

      return {
        ...form,
        ...panels,
        Name: `${form.FirstName} ${form.LastName}`,
        Initial: `${form.FirstName[0]} ${form.LastName[0]}`,
      };
    });
});

const approvedFunctions = computed(
  () => store.state[approvedFunctionsPath()] || {}
);

const signatoryMatrixTableConfig = computed(() => {
  let newS = {
    ...(_signatoryMatrixTableConfig(store, { bulkSelection: state.showLatest, variant: variant.value })),
  };
  return newS;
});

const menuActions = computed(() => {
  const currentRev = store?.state?.[revisionStateKey.value];
  const signatoriesDataPath = signatoriesPath(variant.value);
  const isMilitary = variant.value.key === "military";
  const hasReviewerAccess = isMilitary 
    ? store?.state?.user?.Security?.Military?.Reviewer
    : store?.state?.user?.Security?.Matrices?.Reviewer;
  const hasHeadAccess = isMilitary
    ? store?.state?.user?.Security?.Military?.HeadOfDesign
    : store?.state?.user?.Security?.Matrices?.HeadOfDesign;

  return [
    {
      label: "Add Form",
      action: addEmployee,
    },
    {
      label: `Download Current Matrix (Rev ${currentRev?.Revision})`,
      break: true,
      show() {
        return [!!currentRev?.Signatories, "No signatories in the current revision"];
      },
      action() {
        download(currentRev, { store, form, db });
      },
    },
    {
      label: `Download Next Matrix (Rev ${next(currentRev?.Revision) || 1})`,
      action() {
        download(
          {
            Signatories: store.state[signatoriesDataPath],
            ChangesMade: changeIdentifier(currentRev?.Signatories, store.state[signatoriesDataPath]),
            Revision: `PROPOSED NEXT REVISIONS (${next(currentRev?.Revision) || 1}) - NOT ACCEPTED`,
            Date: new Date(),
          },
          { store, form, db }
        );
      },
      show() {
        return [changesDetected.value, "No changes have been detected"];
      },
    },
    {
      label: `Download Filtered Report (Rev ${currentRev?.Revision})`,
      show() {
        return [!!currentRev?.Signatories, "No signatories in the current revision"];
      },
      "break": true,
      action() {
        const filterModel = table.value?.tableApi?.getFilterModel?.() || {};
        const hasFilters = Object.keys(filterModel).length > 0;
        let idsToInclude = null;
        if (hasFilters) {
          idsToInclude = [];
          table.value.tableApi.forEachNodeAfterFilter(node => {
            if (node.data?.ID) idsToInclude.push(node.data.ID);
          });
        }
        
        
        download(currentRev, { store, form, db }, idsToInclude);
      },
    },
    {
      label: "Download Revision History",
      break: true,
      action() {
        downloadRevisionHistory({ db, store, meta: route.meta });
      },
    },
    {
      label: "View All Revisions",
      break: true,
      async action() {
        const refs = await db.once(revisionRefsPath(variant.value));
        revisionHistoryModal.value.open(refs || {}, variant.value);
      },
    },
    ...viewRevisionChanges.value,
    ...downloadOldRevisions.value,
    {
      label: "HoA / HoD Accept Changes - Revise Signatory Matrix",
      break: true,
      action: reviseSignatoryMatrix,
      show() {
        if (!hasReviewerAccess) {
          return [false, "Must be HoA / HoD to revise the signatory matrix"];
        }
        if (!changesDetected.value) {
          return [false, "No changes have been detected"];
        }
        return [true];
      },
    },
    {
      label: state.showCancelled
        ? "View Active Signatories"
        : "View Inactive Signatories ",
      break: true,
      action() {
        state.showCancelled = !state.showCancelled;
      },
      show() {
        return [hasHeadAccess === true, "You cannot perform this action"];
      },
    },
  ];
});

const downloadOldRevisions = computed(() => {
  const revSnapshotsPath = revisionSnapshotsPath(variant.value);
  return Object.entries(
    store?.state[revSnapshotsPath] || {}
  )
    .filter(([key, value]) => {
      return currentRevision.value?.Revision !== value && currentRevision.value;
    })
    .map(([key, value], index) => {
      return {
        label: `Download Matrix (Rev ${value.Revision})`,
        break: index === 0,
        async action() {
          let rev = await db.once(`${revSnapshotsPath}/${key}`);
          download(rev, { store, form, db });
        },
      };
    });
});

const viewRevisionChanges = computed(() => {
  const revSnapshotsPath = revisionSnapshotsPath(variant.value);
  return Object.entries(
    store?.state[revSnapshotsPath] || {}
  )
    .filter(([key, value]) => {
      return currentRevision.value?.Revision !== value && currentRevision.value;
    })
    .map(([key, value], index) => {
      return {
        label: `View Matrix (Rev ${value.Revision}) Changes`,
        break: index === 0,
        async action() {
          

          form.value.setConfig({
            "title": "List of Revision Changes",
            "type": "view",
            "submitButtonLabel": "Close",
            "fields": [{
              "name": "changes",
              "type": "value",
              "grow": true,
              "divClass": "col-span-3",
              value: () => value.ChangesMade
            }]
          });

          form.value.toggle()
        },
      };
    });
});

const itemSelected = (e, item) => {
  if (e.event?.target?.closest?.(".ag-selection-checkbox, .ag-checkbox-input-wrapper")) return;
  const matrixRoot = variant.value.collectionPrefix 
    ? `/Matrices/${variant.value.collectionPrefix}SignatoryMatrix`
    : `/Matrices/SignatoryMatrix`;
  router.push(`${matrixRoot}/Signatories/${e.data.ID}/${state.showLatest}`);
};

const selectionChanged = (people) => {
  selectedPeople.value = people;
};

const clearSelection = () => {
  table.value?.tableApi?.deselectAll?.();
  selectedPeople.value = [];
};

const completeBulkApproval = () => {
  if (!selectedPeople.value.length) return;
  bulkApprove({
    form: form.value,
    selectedPeople: selectedPeople.value,
    store,
    meta: route.meta
  });
};

const changesDetected = computed(() => {
  const signatoriesDataPath = signatoriesPath(variant.value);
  const currentRev = store?.state?.[revisionStateKey.value];
  return !isEqual(
    currentRev?.Signatories,
    store?.state[signatoriesDataPath]
  );
});

const usersPendingSignOff = computed(() => {
  const signatoriesDataPath = signatoriesPath(variant.value);
  const currentRev = store?.state?.[revisionStateKey.value];
  const live = store.state[signatoriesDataPath] || {};
  const snap = currentRev?.Signatories || {};
  const pending = [];
  for (const [id, liveUser] of Object.entries(live)) {
    const snapUser = snap[id];
    const differs = !snapUser || !isEqual(snapUser, liveUser);
    if (differs && !liveUser?.UserSignOff) {
      pending.push(`${liveUser.FirstName} ${liveUser.LastName}`);
    }
  }
  return pending;
});

const download = (rev, cd, idsToInclude = null) => {
  matrixReport(rev, cd, idsToInclude);
};

const currentRevisionId = computed(
  () => store.state[revisionIdPath(variant.value)]
);
const currentRevision = computed(
  () => store.state[revisionStateKey.value]
);

const changesValue = computed(() =>
  changeIdentifier(
    store?.state?.[revisionStateKey.value]?.Signatories,
    store?.state[signatoriesPath(variant.value)]
  )
);

const openChangesPopup = () => {
  changesPopupOpen.value = true;
};

const closeChangesPopup = () => {
  changesPopupOpen.value = false;
};

const loaded = ref(false)

const getData = async () => {
  const v = variant.value;
  const signatoriesDataPath = signatoriesPath(v);
  const revIdPath = revisionIdPath(v);
  const revSnapshotsPath = revisionSnapshotsPath(v);
  const revStateKey = revisionStateKey.value;
  await Promise.all([
    db.watch(approvedFunctionsPath(), store),
    db.watch(signatoriesDataPath, store),
    db.watch(panelsPath(), store),
    db.watch(typesPath(), store),
    db.watch(revIdPath, store).then(async (id) => {
      await db.watch(
        `${revSnapshotsPath}/${id}`,
        store,
        revStateKey
      );
    }),
  ]);
};

const formSubmitted = async () => {
  await getData();
  clearSelection();
};

watch(
  () => [route.meta.latest, state.showCancelled],
  clearSelection
);

onMounted(async () => {
  await getData();
  loaded.value = true;
});
</script>
